import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Ajax } from '../../components/Ajax';
import { message } from 'antd';
import { useDispatch } from 'react-redux';
import { closePopup, updatePopupTitle } from './../../store/actions';
import { useDelete, useCustomEvent } from '../../project/utilities';
import { validate } from '../Form/validation';
import { useHistory } from 'react-router-dom';
import { showLoader, hideLoader } from '../Loader';
import { v4 as uuidv4 } from 'uuid';
import { T } from '../../components/Translations';

/**
 * custom hook used in detail components to provide the necessary methods
 * @param props contains data, updateUrl, dataLoadUrl, onLoadData, windowKey, nameField, deleteUrl, deleteKeys, backPage, additionalData, listUpdateEvent, validationFields, detailUrl
 * @exports useDetail
 */
export default function useDetail({ data,
    updateUrl,
    dataLoadUrl,
    dataLoadParams,
    onLoadData,
    windowKey,
    nameField,
    deleteUrl,
    deleteKeys,
    backPage,
    backPageRoute,
    backPageParams,
    additionalData,
    listUpdateEvent,
    validationFields,
    changingValidation,
    skipInitialValidation,
    detailUrl,
    reloadEvent,
    detailProps,
    externalValidation }) {
    const dispatch = useDispatch();
    const [dataItem, setDataItem] = useState(null);
    const [validation, setValidation] = useState({});
    const [allowValidation, setAllowValidation] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [loading, setLoading] = useState(false);
    const detailRef = useRef(false);
    const history = useHistory();
    const [loaderName] = useState(uuidv4());
    const loadData = useCallback((id) => {
        if (loading) return;
        setLoading(true);
        Ajax.get({
            url: dataLoadUrl,
            data: dataLoadParams || { id: id || data.ID || "" },
            success: function (response) {
                detailRef.current && ((onLoadData && onLoadData(response, setDataItem)) || setDataItem(response));
                setLoading(false);
            },
            error: function () {
                setLoading(false);
            }
        })
        return true;
    }, [dataLoadUrl, onLoadData, data.ID, detailRef, dataLoadParams, loading]);

    const setReload = useCallback(() => { dataItem && setDataItem({ ...dataItem, reloadData: true }) }, [dataItem]);
    useCustomEvent(reloadEvent, setReload);

    useEffect(() => {
        return () => {
            hideLoader(loaderName);
        };
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        updating || loading ? showLoader(loaderName) : hideLoader(loaderName);
    }, [updating, loading]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        detailRef.current = true;
        setAllowValidation(validationFields && !skipInitialValidation);
        return () => { detailRef.current = false; }
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (dataItem && allowValidation) {
            setValidation(validate({ data: dataItem, validationFields }));
        }
    }, [dataItem, changingValidation && validationFields]);// eslint-disable-line react-hooks/exhaustive-deps


    useEffect(() => {
        if (detailRef.current) {
            (dataLoadUrl && loadData()) ||
                setDataItem(data);
        }
    }, [data.ID]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (detailRef.current) {
            if (dataItem && dataItem.reloadData) {
                loadData(dataItem.ID);
            }
        }
    }, [dataItem && dataItem.reloadData]);// eslint-disable-line react-hooks/exhaustive-deps

    const closeDetailPopup = useCallback(() => {
        dispatch(closePopup(windowKey));
    }, [windowKey, dispatch]);

    const updateData = useCallback((successCallback, errorCallback, options = {}) => { // NOSONAR
        if (updating) return;
        setUpdating(true);
        let validationData = { isValid: true };
        if (!options.skipValidation && validationFields) {
            validationData = validate({ data: dataItem, validationFields });
            setValidation(validationData);
            setAllowValidation(true);
        }
        if (validationData.isValid && (!externalValidation || externalValidation.isValid)) {
            Ajax.post({
                url: updateUrl,
                data: options.overrideData || { ...additionalData, ...dataItem, ...options.additionalData },
                success: function (response) {
                    setUpdating(false);
                    if (response) {
                        if (response.status === 408 || response.status === 504 || response.statusCode === 408 || response.statusCode === 504 || response.ID === undefined) {
                            message.error("Timeout, please update your data again.");
                        } else if (response.HasError) {
                            message.error(response.Message);
                            errorCallback && errorCallback(response);
                        }
                        else {
                            message.success(response.Message);
                            windowKey && detailProps && detailProps.editTitle && dispatch(updatePopupTitle({ windowKey, title: detailProps.editTitle }));
                            successCallback && successCallback(response);
                            if (detailUrl && response.ID !== dataItem.ID) {
                                history.push(detailUrl + "/" + response.ID);
                            }
                        }
                        if (allowValidation && detailRef.current) { setAllowValidation(validationFields && !skipInitialValidation); }
                    }
                },
                error: function (response) {
                    setUpdating(false);
                    message.error(response.message || response.title);
                    errorCallback && errorCallback(response);
                }
            })
        } else {
            message.error(<T>message.fill_in_required_fields</T>);
            setUpdating(false);
            errorCallback && errorCallback();
        }
    }, [dataItem, updateUrl, validationFields, allowValidation, detailUrl, updating, setUpdating, detailProps, windowKey, dispatch, externalValidation, additionalData, history, skipInitialValidation]);

    const onChangeData = useCallback((event, callback) => {
        let newData = { ...dataItem };
        let deepObject = newData;
        let name = event.target.name;
        if (name && name.includes('.')) {
            let deepProps = name.split('.');
            for (let i = 0; i < deepProps.length - 1; i++) {
                deepObject[deepProps[i]] = { ...deepObject[deepProps[i]] };
                deepObject = deepObject[deepProps[i]];
            }
            name = deepProps[deepProps.length - 1];
        }
        if (event.target && name) {
            switch (event.target.type) { // NOSONAR
                case 'checkbox':
                    deepObject[name] = event.target.checked;
                    break;
                default:
                    deepObject[name] = event.target.value;
                    break;
            }
        }
        callback && callback(event, newData);
        setDataItem(newData);
    }, [dataItem]);

    const onDetailDelete = useDelete({
        nameField,
        deleteUrl,
        backPage,
        backPageRoute,
        backPageParams,
        listUpdateEvent,
        deleteKeys
    });

    return { isNew: dataItem && !Boolean(dataItem.ID), dataItem, setDataItem, updateData, onChangeData, closePopup: closeDetailPopup, onDetailDelete, detailRef, validation, updating };
}